import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Fab from '@material-ui/core/Fab';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import withWidth from '@material-ui/core/withWidth';
import React from 'react';
import Errored from '../Errored';
import HalfBasicBackground from '../HalfBasicBackground';
import RequestSentIcon from '../icons/RequestSentIcon';
import Legal from '../Legal';
import { registrationService } from '../services/security/registrationService';
import SmallBackground from '../SmallBackground';
import { globalStyles } from '../style/gstheme';
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH, MAX_USERNAME_LENGTH } from '../utils/constants';
import { makeCancelable, validateEmail, validatePassword, validateUsername } from '../utils/utils';
import AccessHeader from './AccessHeader';

const styles = () => {
    return Object.assign({
        signup: {
            textAlign: 'center',
            position: 'absolute',
            top: 30,
            width: 384,
            padding: 20,
            borderRadius: 5,
            backgroundColor: 'white',
            marginLeft: '50%',
            left: 'calc(25% - 212px)'
        },
        smallSignup: {
            textAlign: 'center',
            position: 'absolute',
            top: '266px',
            width: 384,
            padding: 20,
            borderRadius: 5,
            left: 'calc(50% - 212px)'
        },
    }, globalStyles);
};

class Signup extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            legal: false,
            processing: false,
            checkComplete: false,
            acceptConditions: false,
            isDuplicate: false,
            confirming: false,
            isErrored: null
        };
        this.textInput = React.createRef();
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    checkStatus = async () => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        try {
            const response = await this.doCheckStatus(this.state.registrationID).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            if (response.status === "https://grafsync.graphifi.com/schema/gs/rejected") {
                this.setState({
                    isDuplicate: true,
                    confirming: false,
                    processing: false
                });
            }
            else if (response.status === "https://grafsync.graphifi.com/schema/gs/failed") {
                this.setState({
                    isDuplicate: false,
                    confirming: false,
                    processing: false,
                    isErrored: {
                        message: "Registration failed for some reason. Please contact support."
                    }
                });
            }
            else if (response.status === "https://grafsync.graphifi.com/schema/gs/pending") {
                this.setState({
                    confirming: false,
                    checkComplete: true,
                    processing: false
                });
            }
            else {
                this.hcTimeout = setTimeout(() => {
                    this.checkStatus();
                }, 2000);
            }
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            let message = ["I'm afraid we can't sign you up for some reason."];
            this.setState({
                isErrored: { message },
                confirming: false,
                processing: false
            });
            return;
        }
    };

    handleChangeConditions = (event) => this.setState({ acceptConditions: event.target.checked });

    signup = async () => {
        this.setState({ processing: true }, async () => {
            const { username, email, password } = this.state;
            try {
                const response = await this.doRegistration(username, email, password).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                if (response.status === "duplicate") {
                    this.setState({
                        isDuplicate: true,
                        processing: false,
                    });
                }
                else {
                    // Now we wait for confirmation
                    this.setState({
                        isDuplicate: false,
                        registrationID: response.registrationID,
                        confirming: true
                    }, () => this.hcTimeout = setTimeout(() => { this.checkStatus(); }, 2000));
                }
            }
            catch (e) {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let message = ["I'm afraid we can't sign you up for some reason."];
                if (e.message.startsWith("Graphologi")) {
                    message = e.message; 
                }
                this.setState({
                    isErrored: { message },
                    confirming: false,
                    processing: false
                });
            }
        });
    };

    doRegistration(username, email, password) {
        this.cancellable = makeCancelable(registrationService.signup(username, email, password));
        return this.cancellable;
    }

    doCheckStatus(id) {
        this.cancellable = makeCancelable(registrationService.checkStatus(id));
        return this.cancellable;
    }

    clearError = () => {
        this.setState({ isErrored: null }, () => this.textInput && this.textInput.current && this.textInput.current.focus());
    };

    handleChange = (event) => {
        const t = event.target;
        this.setState({
            [t.name]: t.value,
            isDuplicate: false
        });
    };

    showLegal = () => this.setState({ legal: true });

    exitLegal = () => this.setState({ legal: false });

    render() {

        const { username, email, password, isErrored, confirmPassword, acceptConditions, legal, processing, isDuplicate, confirming, checkComplete } = this.state;
        const { classes } = this.props;
        const validEmail = validateEmail(email);
        const validPassword = validatePassword(password);
        const validUsername = validateUsername(username);
        const disabled = password !== confirmPassword || !validUsername || !validEmail || !validPassword || !acceptConditions || processing || username === "";

        return (
            <React.Fragment>
                <div style={{ zIndex: -1, position: 'fixed', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'white' }} />
                {["md", "lg", "xl"].includes(this.props.width) ?
                    <React.Fragment>
                        <HalfBasicBackground />
                        <div style={{ position: 'fixed', left: '50%', right: 0, top: 0, bottom: 0, backgroundColor: 'white' }} />
                    </React.Fragment>
                    :
                    <React.Fragment>
                        <SmallBackground size={this.props.width} />
                        <div style={{ position: 'absolute', left: 0, right: 0, top: 340, bottom: 0, backgroundColor: 'white' }} />
                    </React.Fragment>
                }
                <div className={["md", "lg","xl"].includes(this.props.width) ?
                    classes.signup
                    :
                    classes.smallSignup
                }>
                    <AccessHeader title="Sign Up" />
                    {checkComplete ?
                        <div style={{ width: '100%', textAlign: 'center', marginTop: 30 }}>
                            <Typography className={classes.mainHeading}>Registration Accepted</Typography>
                            <div style={{ marginTop: 30 }}>
                                <div style={{ marginBottom: '12px' }}>
                                    <RequestSentIcon style={{ width: '36px', height: '32px' }} />
                                </div>
                                <Typography paragraph variant="h3" className={classes.mainHeadingSecondary}>Confirmation Information Sent</Typography>
                            </div>
                            <Typography data-test="instructions" variant="body2">Please check your email.</Typography>
                        </div>
                        :
                        <form autoComplete="off">
                            <div className={classes.loginEntry}>
                                <Typography className={classes.loginSubHeading}>Username</Typography>
                                <TextField className={classes.loginText}
                                    autoComplete="username"
                                    margin="dense"
                                    id="username"
                                    name="username"
                                    disabled={processing}
                                    value={username}
                                    inputRef={this.textInput}
                                    autoFocus={true}
                                    error={isDuplicate || (username.length > 0 && !validUsername)}
                                    variant="outlined"
                                    placeholder="Username"
                                    helperText={isDuplicate ? "Username or email already used." : ""}
                                    fullWidth
                                    onKeyPress={(ev) => {
                                        if (!disabled && ev.charCode === 13) {
                                            this.signup();
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                        }
                                    }}
                                    onChange={this.handleChange}
                                    InputProps={{
                                        className: classes.loginInput,
                                        inputProps: {
                                            "data-test": "username",
                                            className: classes.loginInput,
                                            maxLength: MAX_USERNAME_LENGTH
                                        }
                                    }}
                                />
                                <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Email</Typography>
                                <TextField className={classes.loginText}
                                    autoComplete="new-password"
                                    margin="dense"
                                    id="email"
                                    name="email"
                                    value={email}
                                    disabled={processing}
                                    error={isDuplicate || (email.length > 0 && !validEmail)}
                                    variant="outlined"
                                    placeholder="Email"
                                    fullWidth
                                    onKeyPress={(ev) => {
                                        if (!disabled && ev.charCode === 13) {
                                            this.signup();
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                        }
                                    }}
                                    onChange={this.handleChange}
                                    InputProps={{
                                        className: classes.loginInput,
                                        inputProps: {
                                            "data-test": "email",
                                            className: classes.loginInput,
                                            maxLength: MAX_EMAIL_LENGTH
                                        }
                                    }}
                                />
                                <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Password</Typography>
                                <TextField className={classes.loginText}
                                    autoComplete="new-password"
                                    margin="dense"
                                    id="password"
                                    name="password"
                                    type="password"
                                    disabled={processing}
                                    placeholder="Password"
                                    error={password.length > 0 && !validPassword}
                                    value={password}
                                    variant="outlined"
                                    helperText="Minimum 8 characters including lower case, upper case, a number and a special character from !@#$%^&*"
                                    fullWidth
                                    onChange={this.handleChange}
                                    onKeyPress={(ev) => {
                                        if (!disabled && ev.charCode === 13) {
                                            this.signup();
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                        }
                                    }}
                                    InputProps={{
                                        classes: {
                                            root: classes.loginInput
                                        },
                                        inputProps: {
                                            "data-test": "new-password",
                                            maxLength: MAX_PASSWORD_LENGTH
                                        }
                                    }}
                                />
                                <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Confirm Password</Typography>
                                <TextField className={classes.loginText}
                                    margin="dense"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    disabled={processing}
                                    placeholder="Enter password again"
                                    value={confirmPassword}
                                    variant="outlined"
                                    fullWidth
                                    onChange={this.handleChange}
                                    helperText={password !== confirmPassword ? "Passwords do not match." : ""}
                                    onKeyPress={(ev) => {
                                        if (!disabled && ev.charCode === 13) {
                                            this.signup();
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                        }
                                    }}
                                    InputProps={{
                                        classes: {
                                            root: classes.loginInput
                                        },
                                        inputProps: {
                                            "data-test": "confirm-password",
                                            maxLength: MAX_PASSWORD_LENGTH
                                        }
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', marginTop: 20, justifyContent: 'space-evenly' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ flex: '0 0' }}>
                                            <Checkbox disabled={processing} data-test="accept-ts-and-cs" checked={acceptConditions} onChange={this.handleChangeConditions} name="conditions" />
                                        </div>
                                        <div style={{ flex: '1', paddingTop: 12, textAlign: 'left' }}>
                                            <Typography className={classes.legalLink} style={{ textAlign: 'left' }} onClick={this.showLegal}>Accept Terms and Conditions</Typography>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ flex: '0 0', textAlign: 'right', width: 130 }}>
                                    <Fab
                                        data-test="signup"
                                        disabled={disabled || isDuplicate}
                                        variant="extended"
                                        color="secondary"
                                        style={{ width: 120, marginRight: 20 }}
                                        className={classes.button}
                                        onClick={() => this.signup()}>{
                                            confirming ? "CONFIRMING" : "SIGN UP"
                                        }
                                    </Fab>
                                </div>
                            </div>
                            <Legal open={legal} exitLegal={this.exitLegal} />
                        </form>
                    }
                </div>
                {isErrored && <Errored data={isErrored} clearError={this.clearError} />}
            </React.Fragment>
        );
    }
}

Signup.propTypes = {
    width: PropTypes.string,
    classes: PropTypes.object
};

export default withWidth()(withStyles(styles)(Signup));